import React, { useState, useEffect, useRef } from "react";
import { NavLink, useLocation } from "react-router-dom";
import logo from "../../assets/images/logo/logo.svg";
import "./DashboardLayout.css";

/** Determine which menu should be open for a given path */
const getMenuForPath = (path) => {
  if (path.startsWith("/assets") && !path.startsWith("/assets/items")) {
    return "assets";
  }
  if (
    path.startsWith("/asset-allocations") ||
    path.startsWith("/assets/items")
  ) {
    return "inventory";
  }
  if (
    path.startsWith("/reports/inventory") ||
    path.startsWith("/reports/candidates")
  ) {
    return "reports";
  }
  if (path.startsWith("/asset-requests")) {
    return "assets";
  }
  if (
    path.startsWith("/my-leaves") ||
    path.startsWith("/holidays") ||
    path.startsWith("/leave-approvals")
  ) {
    return "leaves";
  }
  if (path.startsWith("/hr") || path.startsWith("/admin")) {
    return "hr";
  }
  if (path.startsWith("/recruitment")) {
    return "recruitment";
  }
  if (path.startsWith("/reports")) {
    return "reports";
  }
  return null;
};

const Sidebar = ({
  user: userProp,
  isMobileOpen,
  isCollapsed,
  toggleCollapse,
  closeMobileSidebar,
  setShowLogoutModal,
}) => {
  const location = useLocation();

  const initialMenu = getMenuForPath(location.pathname);
  const [openMenus, setOpenMenus] = useState(() => {
    try {
      const saved = JSON.parse(
        localStorage.getItem("worksphere_nav_open") || "[]",
      );
      return initialMenu && !saved.includes(initialMenu)
        ? [...saved, initialMenu]
        : saved;
    } catch {
      return initialMenu ? [initialMenu] : [];
    }
  });

  // Keep local user state for fallback
  const [localUser, setLocalUser] = useState(null);

  // Flyout hover state for collapsed mode
  const [hoveredMenu, setHoveredMenu] = useState(null);
  const leavesRef = useRef(null);
  const assetsRef = useRef(null);
  const inventoryRef = useRef(null);
  const hrRef = useRef(null);
  const recruitmentRef = useRef(null);
  const reportsRef = useRef(null);

  useEffect(() => {
    const userStr = localStorage.getItem("user");
    if (userStr) {
      try {
        setLocalUser(JSON.parse(userStr));
      } catch (e) {
        /* ignore */
      }
    }
  }, []);

  const user = userProp || localUser;

  useEffect(() => {
    const activeMenu = getMenuForPath(location.pathname);
    if (!isCollapsed && activeMenu) {
      setOpenMenus((prev) =>
        prev.includes(activeMenu) ? prev : [...prev, activeMenu],
      );
    }
  }, [location.pathname, isCollapsed]);

  const toggleMenu = (menuKey) => {
    if (isCollapsed) return;
    setOpenMenus((prev) => {
      const next = prev.includes(menuKey)
        ? prev.filter((item) => item !== menuKey)
        : [...prev, menuKey];
      localStorage.setItem("worksphere_nav_open", JSON.stringify(next));
      return next;
    });
  };

  const isFolderActive = (paths) =>
    paths.some((p) => location.pathname === p || location.pathname.startsWith(p + "/"));

  // Role-based access
  const role = (user?.role?.toLowerCase() || "").replace(/\s+/g, "_");

  const isGlobalAdmin =
    role === "admin" ||
    role === "administrator" ||
    role === "ceo" ||
    role === "super_admin" ||
    user?.is_staff === true ||
    user?.is_staff === "true" ||
    user?.is_superuser === true ||
    user?.is_superuser === "true";

  const isHR = role === "hr" || isGlobalAdmin;
  const isITAdmin = role === "it_admin" || isGlobalAdmin;
  const isPM =
    role === "reporting_manager" || role === "project_manager" || isGlobalAdmin;
  const isStaff = isGlobalAdmin || isHR || isITAdmin || isPM;
  const showNavItems = user?.is_verified || isStaff;

  // Shared submenu items
  const leavesSubItems = [
    { to: "/my-leaves", label: "My Leaves" },
    ...(isStaff
      ? [{ to: "/leave-approvals", label: "Pending Approvals" }]
      : []),
    { to: "/holidays", label: "Holidays" },
  ];

  const assetsSubItems = [
    { to: "/assets", label: "My Assets" },
    ...(role !== "it_admin"
      ? [{ to: "/asset-requests/my-requests", label: "My Requests" }]
      : []),
    ...(isStaff
      ? [{ to: "/asset-requests", label: "All Requests", end: true }]
      : []),
  ];

  const inventorySubItems = [
    { to: "/assets/items", label: "Inventory" },
    { to: "/asset-allocations", label: "Allocations" },
  ];

  const hrSubItems = [
    { to: "/hr/dashboard", label: "HR Dashboard" },
    { to: "/hr/employees", label: "Employee Management" },
    { to: "/hr/announcements", label: "HR Announcements" },
    { to: "/hr/holidays", label: "Holiday Management" },
    { to: "/hr/birthdays", label: "Employees Birthday" },
    { to: "/send-email", label: "Direct Email" },
    ...(isGlobalAdmin ? [{ to: "/admin/email-logs", label: "Email Logs" }] : []),
  ];

  const recruitmentSubItems = [
    { to: "/recruitment/candidates", label: "Candidates Tracker" },
  ];

  const reportsSubItems = [
    { to: "/reports/candidates", label: "Candidate Reports" },
    { to: "/reports/leaves", label: "Leave Report" },
    { to: "/reports/leave-balances", label: "Leave Balance Report" },
    { to: "/reports/inventory", label: "Inventory Performance" },
    { to: "/reports/bugs", label: "Bug Reports" },
  ];

  /** Renders a flyout popover for collapsed mode — uses position:fixed to escape overflow clipping */
  const FlyoutMenu = ({ id, title, items, parentRef }) => {
    if (!isCollapsed || hoveredMenu !== id) return null;

    // Calculate position from parent DOM element
    let style = { position: "fixed", zIndex: 2000 };
    if (parentRef?.current) {
      const rect = parentRef.current.getBoundingClientRect();
      style.left = rect.right + 8;
      style.top = rect.top + rect.height / 2;
      style.transform = "translateY(-50%)";
    }

    return (
      <div
        className="flyout-menu"
        style={style}
        onMouseEnter={() => setHoveredMenu(id)}
        onMouseLeave={() => setHoveredMenu(null)}
      >
        <div className="flyout-arrow"></div>
        <div className="flyout-title">{title}</div>
        {items.map((item) => (
          <NavLink
            key={item.to}
            to={item.to}
            end={item.end || false}
            className={({ isActive }) =>
              `flyout-item ${isActive ? "active" : ""}`
            }
            onClick={closeMobileSidebar}
          >
            {item.label}
          </NavLink>
        ))}
      </div>
    );
  };

  return (
    <nav
      className={`sidebar ${isMobileOpen ? "show" : ""} ${isCollapsed ? "collapsed" : ""}`}
    >
      {/* === Logo === */}
      <div className="logo-container border-bottom border-light">
        <div className="logo-icon-wrapper">
          <img src={logo} alt="WorkSphere AI Logo" className="logo-img" />
        </div>
        {!isCollapsed && (
          <div className="logo-text-wrapper">
            <h1 className="logo-text mb-0">
              WorkSphere<span className="ai-badge">AI</span>
            </h1>
          </div>
        )}

        {/* Mobile close button */}
        <button
          className="btn btn-sm d-lg-none ms-auto"
          onClick={closeMobileSidebar}
          aria-label="Close sidebar"
        >
          <i className="bi bi-x-lg"></i>
        </button>
      </div>

      {/* Collapse / Expand toggle — floats on sidebar edge */}
      <button
        className="collapse-toggle-btn d-none d-lg-flex"
        onClick={toggleCollapse}
        title={isCollapsed ? "Expand Sidebar" : "Collapse Sidebar"}
        aria-label={isCollapsed ? "Expand Sidebar" : "Collapse Sidebar"}
      >
        <i
          className={`bi ${isCollapsed ? "bi-chevron-right" : "bi-chevron-left"}`}
        ></i>
      </button>

      {/* === Navigation === */}
      <div className="sidebar-nav-list mt-3 flex-grow-1">
        {showNavItems && (
          <NavLink
            to="/dashboard"
            className={({ isActive }) => `nav-link ${isActive ? "active" : ""}`}
          >
            <i className="bi bi-speedometer2"></i>
            {!isCollapsed ? (
              <span>Dashboard</span>
            ) : (
              <span className="sidebar-tooltip">Dashboard</span>
            )}
          </NavLink>
        )}

        {/* My Profile - Always visible */}
        <NavLink
          to="/profile"
          className={({ isActive }) => `nav-link ${isActive ? "active" : ""}`}
        >
          <i className="bi bi-person-badge"></i>
          {!isCollapsed ? (
            <span>My Profile</span>
          ) : (
            <span className="sidebar-tooltip">My Profile</span>
          )}
        </NavLink>

        {showNavItems && (
          <>
            {/* HR Policies - Always visible for verified users */}
            <NavLink
              to="/hr-policies"
              className={({ isActive }) =>
                `nav-link ${isActive ? "active" : ""}`
              }
              onClick={closeMobileSidebar}
            >
              <i className="bi bi-shield-check"></i>
              {!isCollapsed ? (
                <span>HR Policies</span>
              ) : (
                <span className="sidebar-tooltip">HR Policies</span>
              )}
            </NavLink>
            
            {/* Report Bug - Premium Support Access */}
            <NavLink
              to="/bug-report"
              className={({ isActive }) =>
                `nav-link ${isActive ? "active" : ""}`
              }
              onClick={closeMobileSidebar}
            >
              <i className="bi bi-bug"></i>
              {!isCollapsed ? (
                <span>Report Bug</span>
              ) : (
                <span className="sidebar-tooltip">Report Bug</span>
              )}
            </NavLink>

            {/* === Leaves === */}
            <div
              ref={leavesRef}
              className={`nav-link folder-link ${openMenus.includes("leaves") && !isCollapsed ? "menu-open" : ""} ${isFolderActive(["/my-leaves", "/holidays", "/leave-approvals"]) ? "active-folder" : ""}`}
              onClick={() => toggleMenu("leaves")}
              onMouseEnter={() => isCollapsed && setHoveredMenu("leaves")}
              onMouseLeave={() => isCollapsed && setHoveredMenu(null)}
              role="button"
              tabIndex={0}
            >
              <i className="bi bi-calendar-check"></i>
              {!isCollapsed ? (
                <>
                  <span>Leaves</span>
                  <i
                    className={`bi bi-chevron-down chevron-icon ${openMenus.includes("leaves") ? "rotate-180" : ""}`}
                  ></i>
                </>
              ) : (
                hoveredMenu !== "leaves" && (
                  <span className="sidebar-tooltip">Leaves</span>
                )
              )}
              <FlyoutMenu
                id="leaves"
                title="Leaves"
                items={leavesSubItems}
                parentRef={leavesRef}
              />
            </div>

            {!isCollapsed && (
              <div
                className={`submenu-wrapper ${openMenus.includes("leaves") ? "open" : ""}`}
              >
                <div className="submenu-inner">
                  {leavesSubItems.map((item) => (
                    <NavLink
                      key={item.to}
                      to={item.to}
                      className={({ isActive }) =>
                        `nav-link sub-nav-link ${isActive ? "active" : ""}`
                      }
                      onClick={closeMobileSidebar}
                    >
                      <span className="sub-dot"></span> {item.label}
                    </NavLink>
                  ))}
                </div>
              </div>
            )}

            {/* === Assets === */}
            <div
              ref={assetsRef}
              className={`nav-link folder-link ${openMenus.includes("assets") && !isCollapsed ? "menu-open" : ""} ${
                location.pathname === "/assets" ||
                (location.pathname.startsWith("/asset-requests") &&
                  !location.pathname.includes("analytics"))
                  ? "active-folder"
                  : ""
              }`}
              onClick={() => toggleMenu("assets")}
              onMouseEnter={() => isCollapsed && setHoveredMenu("assets")}
              onMouseLeave={() => isCollapsed && setHoveredMenu(null)}
              role="button"
              tabIndex={0}
            >
              <i className="bi bi-laptop"></i>
              {!isCollapsed ? (
                <>
                  <span>Assets</span>
                  <i
                    className={`bi bi-chevron-down chevron-icon ${openMenus.includes("assets") ? "rotate-180" : ""}`}
                  ></i>
                </>
              ) : (
                hoveredMenu !== "assets" && (
                  <span className="sidebar-tooltip">Assets</span>
                )
              )}
              <FlyoutMenu
                id="assets"
                title="Assets"
                items={assetsSubItems}
                parentRef={assetsRef}
              />
            </div>

            {!isCollapsed && (
              <div
                className={`submenu-wrapper ${openMenus.includes("assets") ? "open" : ""}`}
              >
                <div className="submenu-inner">
                  {assetsSubItems.map((item) => (
                    <NavLink
                      key={item.to}
                      to={item.to}
                      end={item.end || false}
                      className={({ isActive }) =>
                        `nav-link sub-nav-link ${isActive ? "active" : ""}`
                      }
                      onClick={closeMobileSidebar}
                    >
                      <span className="sub-dot"></span> {item.label}
                    </NavLink>
                  ))}
                </div>
              </div>
            )}

            {/* === Inventory (IT Admin Only) === */}
            {isITAdmin && (
              <>
                <div
                  ref={inventoryRef}
                  className={`nav-link folder-link ${openMenus.includes("inventory") && !isCollapsed ? "menu-open" : ""} ${isFolderActive(["/asset-allocations", "/assets/items", "/asset-requests/analytics"]) ? "active-folder" : ""}`}
                  onClick={() => toggleMenu("inventory")}
                  onMouseEnter={() =>
                    isCollapsed && setHoveredMenu("inventory")
                  }
                  onMouseLeave={() => isCollapsed && setHoveredMenu(null)}
                  role="button"
                  tabIndex={0}
                >
                  <i className="bi bi-boxes"></i>
                  {!isCollapsed ? (
                    <>
                      <span>Inventory</span>
                      <i
                        className={`bi bi-chevron-down chevron-icon ${openMenus.includes("inventory") ? "rotate-180" : ""}`}
                      ></i>
                    </>
                  ) : (
                    hoveredMenu !== "inventory" && (
                      <span className="sidebar-tooltip">Inventory</span>
                    )
                  )}
                  <FlyoutMenu
                    id="inventory"
                    title="Inventory"
                    items={inventorySubItems}
                    parentRef={inventoryRef}
                  />
                </div>

                {!isCollapsed && (
                  <div
                    className={`submenu-wrapper ${openMenus.includes("inventory") ? "open" : ""}`}
                  >
                    <div className="submenu-inner">
                      {inventorySubItems.map((item) => (
                        <NavLink
                          key={item.to}
                          to={item.to}
                          className={({ isActive }) =>
                            `nav-link sub-nav-link ${isActive ? "active" : ""}`
                          }
                          onClick={closeMobileSidebar}
                        >
                          <span className="sub-dot"></span> {item.label}
                        </NavLink>
                      ))}
                    </div>
                  </div>
                )}
              </>
            )}
            {/* === HR Management (HR Only) === */}
            {(isHR || user?.is_staff) && (
              <>
                <div
                  ref={hrRef}
                  className={`nav-link folder-link ${openMenus.includes("hr") && !isCollapsed ? "menu-open" : ""} ${isFolderActive(["/hr", "/admin"]) ? "active-folder" : ""}`}
                  onClick={() => toggleMenu("hr")}
                  onMouseEnter={() => isCollapsed && setHoveredMenu("hr")}
                  onMouseLeave={() => isCollapsed && setHoveredMenu(null)}
                  role="button"
                  tabIndex={0}
                >
                  <i className="bi bi-person-gear"></i>
                  {!isCollapsed ? (
                    <>
                      <span>HR Management</span>
                      <i
                        className={`bi bi-chevron-down chevron-icon ${openMenus.includes("hr") ? "rotate-180" : ""}`}
                      ></i>
                    </>
                  ) : (
                    hoveredMenu !== "hr" && (
                      <span className="sidebar-tooltip">HR Management</span>
                    )
                  )}
                  <FlyoutMenu
                    id="hr"
                    title="HR Management"
                    items={hrSubItems}
                    parentRef={hrRef}
                  />
                </div>

                {!isCollapsed && (
                  <div
                    className={`submenu-wrapper ${openMenus.includes("hr") ? "open" : ""}`}
                  >
                    <div className="submenu-inner">
                      {hrSubItems.map((item) => (
                        <NavLink
                          key={item.to}
                          to={item.to}
                          className={({ isActive }) =>
                            `nav-link sub-nav-link ${isActive ? "active" : ""}`
                          }
                          onClick={closeMobileSidebar}
                        >
                          <span className="sub-dot"></span> {item.label}
                        </NavLink>
                      ))}
                    </div>
                  </div>
                )}
              </>
            )}

            {/* === Recruitment (HR/Admin Only) === */}
            {isHR && (
              <>
                <div
                  ref={recruitmentRef}
                  className={`nav-link folder-link recruitment-folder ${openMenus.includes("recruitment") && !isCollapsed ? "menu-open" : ""} ${isFolderActive(["/recruitment", "/reports/candidates"]) ? "active-folder" : ""}`}
                  onClick={() => {
                    toggleMenu("recruitment");
                    // Mark as dismissed when clicked
                    const dismissed = JSON.parse(
                      localStorage.getItem("worksphere_new_badge_dismissed") ||
                        "[]",
                    );
                    if (!dismissed.includes("recruitment")) {
                      dismissed.push("recruitment");
                      localStorage.setItem(
                        "worksphere_new_badge_dismissed",
                        JSON.stringify(dismissed),
                      );
                    }
                  }}
                  onMouseEnter={() =>
                    isCollapsed && setHoveredMenu("recruitment")
                  }
                  onMouseLeave={() => isCollapsed && setHoveredMenu(null)}
                  role="button"
                  tabIndex={0}
                >
                  <i className="bi bi-clipboard-check"></i>
                  {!isCollapsed ? (
                    <>
                      <span>Recruitment</span>
                      {!JSON.parse(
                        localStorage.getItem(
                          "worksphere_new_badge_dismissed",
                        ) || "[]",
                      ).includes("recruitment") && (
                        <span className="badge-new-sidebar">NEW</span>
                      )}
                      <i
                        className={`bi bi-chevron-down chevron-icon ${openMenus.includes("recruitment") ? "rotate-180" : ""}`}
                      ></i>
                    </>
                  ) : (
                    hoveredMenu !== "recruitment" && (
                      <span className="sidebar-tooltip">Recruitment</span>
                    )
                  )}
                  <FlyoutMenu
                    id="recruitment"
                    title="Recruitment"
                    items={recruitmentSubItems}
                    parentRef={recruitmentRef}
                  />
                </div>

                {!isCollapsed && (
                  <div
                    className={`submenu-wrapper ${openMenus.includes("recruitment") ? "open" : ""}`}
                  >
                    <div className="submenu-inner">
                      {recruitmentSubItems.map((item) => (
                        <NavLink
                          key={item.to}
                          to={item.to}
                          className={({ isActive }) =>
                            `nav-link sub-nav-link ${isActive ? "active" : ""}`
                          }
                          onClick={closeMobileSidebar}
                        >
                          <span className="sub-dot"></span> {item.label}
                        </NavLink>
                      ))}
                    </div>
                  </div>
                )}
              </>
            )}

            {/* === Reports (HR/Admin/PM) === */}
            {(isHR || isGlobalAdmin || user?.is_staff) && (
              <>
                <div
                  ref={reportsRef}
                  className={`nav-link folder-link ${openMenus.includes("reports") && !isCollapsed ? "menu-open" : ""} ${isFolderActive(["/reports"]) ? "active-folder" : ""}`}
                  onClick={() => toggleMenu("reports")}
                  onMouseEnter={() => isCollapsed && setHoveredMenu("reports")}
                  onMouseLeave={() => isCollapsed && setHoveredMenu(null)}
                  role="button"
                  tabIndex={0}
                >
                  <i className="bi bi-file-earmark-bar-graph"></i>
                  {!isCollapsed ? (
                    <>
                      <span>Reports</span>
                      <i
                        className={`bi bi-chevron-down chevron-icon ${openMenus.includes("reports") ? "rotate-180" : ""}`}
                      ></i>
                    </>
                  ) : (
                    hoveredMenu !== "reports" && (
                      <span className="sidebar-tooltip">Reports</span>
                    )
                  )}
                  <FlyoutMenu
                    id="reports"
                    title="Reports"
                    items={reportsSubItems}
                    parentRef={reportsRef}
                  />
                </div>

                {!isCollapsed && (
                  <div
                    className={`submenu-wrapper ${openMenus.includes("reports") ? "open" : ""}`}
                  >
                    <div className="submenu-inner">
                      {reportsSubItems.map((item) => (
                        <NavLink
                          key={item.to}
                          to={item.to}
                          className={({ isActive }) =>
                            `nav-link sub-nav-link ${isActive ? "active" : ""}`
                          }
                          onClick={closeMobileSidebar}
                        >
                          <span className="sub-dot"></span> {item.label}
                        </NavLink>
                      ))}
                    </div>
                  </div>
                )}
              </>
            )}

            {/* Settings — visible to Admins only */}
            {(role === "admin" ||
              role === "administrator" ||
              role === "super_admin" ||
              user?.is_staff === true ||
              user?.is_staff === "true" ||
              user?.is_superuser === true ||
              user?.is_superuser === "true") && (
              <NavLink
                to="/settings"
                className={({ isActive }) =>
                  `nav-link ${isActive ? "active" : ""}`
                }
                onClick={closeMobileSidebar}
              >
                <i className="bi bi-gear"></i>
                {!isCollapsed ? (
                  <span>Settings</span>
                ) : (
                  <span className="sidebar-tooltip">Settings</span>
                )}
              </NavLink>
            )}
          </>
        )}
      </div>

      {/* === User Profile Footer === */}
      <div className="sidebar-profile mt-auto">
        <div
          className="profile-card"
          onClick={() => isCollapsed && setShowLogoutModal(true)}
          role={isCollapsed ? "button" : undefined}
          title={
            isCollapsed
              ? `${user?.first_name || "User"} — Click to logout`
              : undefined
          }
        >
          <div className="profile-avatar">
            {user?.first_name?.[0] || user?.username?.[0] || "?"}
            {user?.last_name?.[0] || ""}
          </div>
          {!isCollapsed && (
            <>
              <div className="profile-info">
                <h6
                  className="profile-name fw-bold"
                  style={{
                    fontSize: "0.85rem",
                    marginBottom: "0px",
                    whiteSpace: "nowrap",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                  }}
                >
                  {user?.designation_name || user?.designation || "Employee"}
                </h6>
                <div
                  className="profile-role text-muted"
                  style={{ fontSize: "0.72rem" }}
                >
                  {user?.employee_id || ""}
                </div>
              </div>
              <button
                className="logout-btn"
                onClick={(e) => {
                  e.stopPropagation();
                  setShowLogoutModal(true);
                }}
                title="Logout"
                aria-label="Logout"
              >
                <i className="bi bi-box-arrow-right"></i>
              </button>
            </>
          )}
        </div>
      </div>
    </nav>
  );
};

export default Sidebar;
